import { useRef } from "react";
import { useCarried } from "./useCarried";
import { useFutureCallback } from "./useFutureCallback";

const { setTimeout, clearTimeout } = window;

export interface DebounceOptions {
  /** The number of milliseconds before executing the callback. */
  delay?: number;
  /** If `true`, execute the callback immediately, and wait 'delay' before executing it again. */
  immediate?: boolean;
  /** If `true`, don't restart the timer everytime an invocation is attempted.
   * When `false`, the function must not be invoked for at least the given 'delay' before the callback will actually execute. */
  waitOnce?: boolean;
}

export const useDebounce = <Args extends unknown[]>(
  callback: (...args: Args) => void,
  options: DebounceOptions | number = 0
) => {
  if (typeof options === "number") options = { delay: options };
  options = useCarried(options);
  // be sure to use any newer version of the callback after the delay:
  const callbackStore = useCarried({ callback });

  const timer = useRef<number>(-1);

  return useFutureCallback((...args: Args) => {
    if (timer.current < 0 || !options.waitOnce) {
      clearTimeout(timer.current);

      timer.current = setTimeout(() => {
        timer.current = -1;

        if (!options.immediate) callbackStore.callback(...args);
      }, options.delay ?? 0);
    }

    if (options.immediate) callbackStore.callback(...args);
  });
};
